import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion, useInView } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Menu, X, Shield, Star, ChevronRight, Check, Phone, Video, Rocket, MessageCircle, Monitor, Tablet, Smartphone, Cloud, BarChart3, ChefHat } from "lucide-react";

import logoAltus from "@/assets/logo-altus.png";
import screenshotCmv from "@/assets/screenshot-cmv.jpeg";
import screenshotPdv from "@/assets/screenshot-pdv.jpeg";
import screenshotTablet from "@/assets/screenshot-tablet.jpeg";
import screenshotGarcom from "@/assets/screenshot-garcom.jpeg";

// ── Color palette — hybrid light/dark ──
const C = {
  // Dark sections
  dark: "#0a0e1a",
  darkSurface: "#0d1226",
  darkCard: "#111829",
  darkBorder: "#1e2d52",
  // Light sections
  white: "#ffffff",
  lightBg: "#f7f9fc",
  lightBorder: "#e2e8f0",
  // Brand
  blue: "#00aaff",
  cyan: "#4dd0ff",
  fill: "#0a3a6e",
  fillBorder: "#0077cc",
  // Status
  green: "#00e676",
  greenBg: "#0a3d1e",
  greenBorder: "#00c853",
  amber: "#ef9f27",
  amberBg: "#2a1a0a",
  amberBorder: "#8a4a0a",
  // Text — light bg
  textDark: "#1a2332",
  textSecondary: "#5a6b7f",
  textTertiary: "#8896a8",
  // Text — dark bg
  textLight: "#c8dff5",
  textMuted: "#7aabdd",
  textSubtle: "#4a7abf",
  textGhost: "#3a5a8c",
};

const whatsappUrl = "https://wa.me/5516997096672";

// ── Logo ──
const AltusLogo = ({ size = 38, showText = true }: { size?: number; showText?: boolean }) => (
  <div className="flex items-center gap-2">
    <img src={logoAltus} alt="AltusFood" width={size} height={size} className="rounded-lg" />
    {showText && (
      <div className="flex items-baseline gap-0.5">
        <span style={{ fontFamily: "'Exo 2'", fontWeight: 800, color: C.blue, letterSpacing: "3px", fontSize: size > 40 ? "38px" : "18px" }}>
          ALTUS
        </span>
        <span style={{ fontFamily: "'Exo 2'", fontWeight: 400, color: C.cyan, fontSize: size > 40 ? "16px" : "11px", letterSpacing: "2px" }}>
          Food
        </span>
      </div>
    )}
  </div>
);

// ── WhatsApp SVG icon ──
const WaSvg = ({ color = C.green, size = 20 }: { color?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill={color}/>
    <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.96 7.96 0 01-4.108-1.14L4 20l1.14-3.892A7.96 7.96 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" fill={color}/>
  </svg>
);

// ── CountUp ──
function CountUp({ target, suffix = "", prefix = "" }: { target: number; suffix?: string; prefix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1500;
    const step = target / (duration / 16);
    const id = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(id); }
      else setVal(Math.floor(start));
    }, 16);
    return () => clearInterval(id);
  }, [inView, target]);

  return <span ref={ref}>{prefix}{val.toLocaleString("pt-BR")}{suffix}</span>;
}

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

// ── Form types ──
interface LeadForm {
  nome: string;
  email: string;
  whatsapp: string;
  cidade: string;
  estabelecimento: string;
  tipo: string;
  mensagem: string;
}

// ════════════════════════════════════════════════════════════
// LANDING PAGE
// ════════════════════════════════════════════════════════════

const Landing = () => {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formSent, setFormSent] = useState(false);
  const [formError, setFormError] = useState("");
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<LeadForm>();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navLinks = [
    { label: "Funcionalidades", href: "#funcionalidades" },
    { label: "Planos", href: "#planos" },
    { label: "Suporte", href: "#suporte" },
    { label: "Contato", href: "#contato" },
  ];

  const scrollTo = (href: string) => {
    setMobileMenu(false);
    const el = document.querySelector(href);
    el?.scrollIntoView({ behavior: "smooth" });
  };

  const onSubmit = async (data: LeadForm) => {
    try {
      setFormError("");
      const { error } = await supabase.from("leads" as any).insert({
        nome: data.nome,
        email: data.email || null,
        whatsapp: data.whatsapp || null,
        cidade: data.cidade || null,
        estabelecimento: data.estabelecimento || null,
        tipo: data.tipo || null,
        mensagem: data.mensagem || null,
      });
      if (error) throw error;
      setFormSent(true);
      reset();
    } catch {
      setFormError("Erro ao enviar. Tente novamente ou fale pelo WhatsApp.");
    }
  };

  // ════════════════════════════════════════════════════════
  // NAVBAR — white/transparent
  // ════════════════════════════════════════════════════════
  const Navbar = () => (
    <motion.nav
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed top-0 left-0 right-0 z-50 h-[64px] flex items-center px-4 md:px-8 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(255,255,255,0.97)" : "transparent",
        borderBottom: scrolled ? `1px solid ${C.lightBorder}` : "1px solid transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
      }}
    >
      <div className="max-w-6xl mx-auto w-full flex items-center justify-between">
        <AltusLogo size={114} showText={false} />

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map(l => (
            <button key={l.href} onClick={() => scrollTo(l.href)}
              className="text-[13px] font-medium transition-colors"
              style={{ fontFamily: "Nunito", color: scrolled ? C.textSecondary : "rgba(255,255,255,0.8)" }}>
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/auth" className="px-4 py-2 rounded-lg text-[13px] font-medium transition-colors"
            style={{ color: scrolled ? C.textSecondary : "rgba(255,255,255,0.8)", border: scrolled ? `1px solid ${C.lightBorder}` : "1px solid rgba(255,255,255,0.2)" }}>
            Entrar
          </Link>
          <button onClick={() => scrollTo("#planos")}
            className="px-5 py-2 rounded-lg text-[13px] font-bold transition-all hover:brightness-110 text-white"
            style={{ background: C.blue }}>
            Assinar agora
          </button>
        </div>

        {/* Mobile hamburger */}
        <button className="md:hidden" onClick={() => setMobileMenu(!mobileMenu)}
          style={{ color: scrolled ? C.textDark : "white" }}>
          {mobileMenu ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenu && (
        <div className="absolute top-[64px] left-0 right-0 p-4 flex flex-col gap-3 md:hidden shadow-lg"
          style={{ background: C.white, borderBottom: `1px solid ${C.lightBorder}` }}>
          {navLinks.map(l => (
            <button key={l.href} onClick={() => scrollTo(l.href)}
              className="text-left text-sm py-2 font-medium" style={{ color: C.textSecondary }}>
              {l.label}
            </button>
          ))}
          <Link to="/auth" className="text-sm py-2" style={{ color: C.textSecondary }}>Entrar</Link>
          <button onClick={() => scrollTo("#planos")}
            className="py-2.5 px-4 rounded-lg text-sm font-bold text-white"
            style={{ background: C.blue }}>
            Assinar agora
          </button>
        </div>
      )}
    </motion.nav>
  );

  // ════════════════════════════════════════════════════════
  // HERO — dark gradient
  // ════════════════════════════════════════════════════════
  const Hero = () => (
    <section className="relative pt-[64px] overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.dark} 0%, #0f1a30 50%, #0a1525 100%)` }}>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
        {/* LEFT */}
        <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
          className="flex flex-col gap-5 relative z-10">
          <AltusLogo size={128} showText={false} />

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full w-fit text-xs"
            style={{ background: "rgba(0,170,255,0.1)", border: "1px solid rgba(0,170,255,0.3)", color: C.cyan }}>
            <span className="w-2 h-2 rounded-full" style={{ background: C.green }} />
            Sistema de gestão 100% nuvem para restaurantes
          </div>

          <h1 style={{ fontFamily: "'Exo 2'", fontWeight: 800, fontSize: "clamp(28px,4vw,42px)", color: "white", lineHeight: 1.15 }}>
            O sistema que seu restaurante{" "}
            <span style={{ color: C.blue }}>merecia ter</span>
          </h1>

          <p style={{ fontFamily: "'Exo 2'", fontSize: 16, color: C.cyan, letterSpacing: "3px", fontWeight: 400 }}>
            Seja Altus.
          </p>

          <p className="text-base leading-relaxed" style={{ fontFamily: "Nunito", color: "rgba(200,223,245,0.8)" }}>
            PDV, app do garçom, KDS, tablet de autoatendimento, delivery e relatórios gerenciais — tudo integrado, sem complicação.
          </p>

          {/* CTA */}
          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => scrollTo("#planos")}
              className="px-7 py-3.5 rounded-xl font-bold text-[15px] transition-all hover:brightness-110 hover:shadow-lg text-white"
              style={{ background: C.blue, boxShadow: "0 4px 20px rgba(0,170,255,0.35)" }}>
              Assinar agora
            </button>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
              className="px-5 py-3.5 rounded-xl font-semibold text-sm transition-all hover:brightness-110 inline-flex items-center gap-2"
              style={{ background: C.greenBg, border: `1px solid ${C.greenBorder}`, color: C.green }}>
              <WaSvg size={18} />
              WhatsApp
            </a>
          </div>

          {/* Trust */}
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs mt-2" style={{ color: "rgba(200,223,245,0.7)" }}>
            {[
              { dot: C.green, text: "A partir de R$149/mês" },
              { dot: C.green, text: "Sem fidelidade" },
              { dot: C.amber, text: "7 dias ou dinheiro de volta" },
            ].map(t => (
              <span key={t.text} className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.dot }} />
                {t.text}
              </span>
            ))}
          </div>
        </motion.div>

        {/* RIGHT — Multi-device composition */}
        <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="hidden md:flex items-end justify-center relative" style={{ minHeight: 420 }}>
          {/* Monitor — PDV */}
          <div className="relative z-10 rounded-xl overflow-hidden shadow-2xl" style={{ width: 340, background: "#1a1a2e", border: "3px solid #2a3050" }}>
            <div className="flex items-center gap-1.5 px-3 py-1.5" style={{ background: "#12182a" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: "#ef4444" }} />
              <span className="w-2 h-2 rounded-full" style={{ background: "#eab308" }} />
              <span className="w-2 h-2 rounded-full" style={{ background: "#22c55e" }} />
              <span className="ml-2 text-[9px]" style={{ color: C.textSubtle }}>PDV AltusFood</span>
            </div>
            <img src={screenshotPdv} alt="PDV AltusFood" className="w-full object-cover" style={{ height: 220 }} />
          </div>

          {/* Tablet — overlapping right */}
          <div className="absolute -right-2 bottom-0 z-20 rounded-xl overflow-hidden shadow-2xl" style={{ width: 180, background: "#1a1a2e", border: "3px solid #2a3050" }}>
            <div className="text-center py-1 text-[8px] font-semibold" style={{ color: C.blue, background: "#12182a" }}>
              Autoatendimento
            </div>
            <img src={screenshotTablet} alt="Tablet autoatendimento" className="w-full object-cover" style={{ height: 200 }} />
          </div>

          {/* Phone — overlapping left */}
          <div className="absolute -left-2 bottom-4 z-20 rounded-xl overflow-hidden shadow-2xl" style={{ width: 110, background: "#1a1a2e", border: "3px solid #2a3050", borderRadius: 16 }}>
            <div className="text-center py-1 text-[7px] font-semibold" style={{ color: C.green, background: "#12182a" }}>
              App Garçom
            </div>
            <img src={screenshotGarcom} alt="App do garçom" className="w-full object-cover" style={{ height: 170 }} />
          </div>
        </motion.div>
      </div>

      {/* Subtle gradient fade to white */}
      <div className="h-16" style={{ background: `linear-gradient(to bottom, transparent, ${C.white})` }} />
    </section>
  );

  // ════════════════════════════════════════════════════════
  // STATS — light background
  // ════════════════════════════════════════════════════════
  const Stats = () => {
    const stats = [
      { value: 500, prefix: "+", label: "Estabelecimentos ativos" },
      { value: 2, suffix: "M+", label: "Pedidos processados" },
      { value: 99.9, suffix: "%", label: "Uptime garantido" },
      { value: 149, prefix: "R$", label: "A partir de por mês" },
    ];
    return (
      <section className="py-10 px-4" style={{ background: C.white }}>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center py-4">
              <p style={{ fontFamily: "'Exo 2'", fontSize: 32, fontWeight: 800, color: C.blue }}>
                <CountUp target={s.value} prefix={s.prefix} suffix={s.suffix} />
              </p>
              <p className="text-sm mt-1" style={{ color: C.textSecondary }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>
    );
  };

  // ════════════════════════════════════════════════════════
  // FEATURES — white bg, cards with shadow
  // ════════════════════════════════════════════════════════
  const Features = () => {
    const features = [
      { icon: <Smartphone size={24} />, title: "App do Garçom", desc: "Pedidos pelo celular em tempo real. Funciona em Android e SmartPOS de adquirentes como Stone e PagSeguro.", img: screenshotGarcom, color: "#10b981" },
      { icon: <Monitor size={24} />, title: "PDV Completo", desc: "Mesa, comanda, balcão e delivery. Interface touch otimizada para monitores de 15 a 24 polegadas.", img: screenshotPdv, color: C.blue },
      { icon: <ChefHat size={24} />, title: "KDS — Cozinha Digital", desc: "Display na cozinha mostra pedidos em tempo real. Zero papel, zero erro, máxima agilidade.", color: "#f59e0b" },
      { icon: <Tablet size={24} />, title: "Tablet Autoatendimento", desc: "Cliente faz o pedido direto da mesa pelo tablet. Aumenta o ticket médio e libera os garçons.", img: screenshotTablet, color: "#8b5cf6" },
      { icon: <BarChart3 size={24} />, title: "CMV e DRE", desc: "Custo de mercadoria vendida por produto, margem bruta e DRE mensal. Gestão real do seu negócio.", img: screenshotCmv, color: "#ef4444" },
      { icon: <Cloud size={24} />, title: "100% Nuvem", desc: "Acesse de qualquer lugar, qualquer dispositivo. Backup automático, sem servidor local.", color: "#06b6d4" },
    ];

    return (
      <section id="funcionalidades" className="py-16 md:py-24 px-4 md:px-8" style={{ background: C.white }}>
        <div className="max-w-6xl mx-auto">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="space-y-10">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-xs font-bold tracking-[3px] uppercase mb-3" style={{ color: C.blue }}>Funcionalidades</p>
              <h2 style={{ fontFamily: "'Exo 2'", fontWeight: 800, fontSize: "clamp(24px,3vw,32px)", color: C.textDark }}>
                Tudo que seu negócio precisa{" "}
                <span style={{ color: C.blue }}>em um único sistema</span>
              </h2>
              <p className="mt-3 text-base" style={{ color: C.textSecondary }}>
                Do pedido ao relatório financeiro. Tecnologia que simplifica sua operação.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {features.map((f, i) => (
                <motion.div key={i} variants={fadeUp}
                  className="group rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 cursor-default"
                  style={{
                    background: C.white,
                    border: `1px solid ${C.lightBorder}`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 30px rgba(0,0,0,0.1)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: `${f.color}12`, color: f.color }}>
                    {f.icon}
                  </div>
                  <h3 className="text-base font-bold mb-2" style={{ color: C.textDark, fontFamily: "'Exo 2'" }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: C.textSecondary }}>{f.desc}</p>
                  {f.img && (
                    <img src={f.img} alt={f.title} className="mt-4 rounded-xl w-full object-cover transition-transform group-hover:scale-[1.02]"
                      style={{ maxHeight: 160, border: `1px solid ${C.lightBorder}` }} />
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  // ════════════════════════════════════════════════════════
  // SYSTEM IN ACTION — light gray bg
  // ════════════════════════════════════════════════════════
  const SystemScreens = () => (
    <section className="py-16 md:py-24 px-4 md:px-8" style={{ background: C.lightBg }}>
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-12">
          <p className="text-xs font-bold tracking-[3px] uppercase mb-3" style={{ color: C.blue }}>O Sistema em Ação</p>
          <h2 style={{ fontFamily: "'Exo 2'", fontWeight: 800, fontSize: "clamp(24px,3vw,32px)", color: C.textDark }}>
            Veja como funciona <span style={{ color: C.blue }}>na prática</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* PDV — large card */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="md:col-span-2 rounded-2xl overflow-hidden"
            style={{ background: C.white, border: `1px solid ${C.lightBorder}`, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.lightBorder}` }}>
              <div className="flex items-center gap-2">
                <Monitor size={16} style={{ color: C.blue }} />
                <p className="text-sm font-bold" style={{ color: C.textDark, fontFamily: "'Exo 2'" }}>PDV — Ponto de Venda</p>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${C.blue}15`, color: C.blue }}>Desktop / Touch</span>
            </div>
            <img src={screenshotPdv} alt="PDV AltusFood" className="w-full object-cover" style={{ maxHeight: 340 }} />
          </motion.div>

          {/* App Garçom */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="rounded-2xl overflow-hidden"
            style={{ background: C.white, border: `1px solid ${C.lightBorder}`, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
            <div className="p-4" style={{ borderBottom: `1px solid ${C.lightBorder}` }}>
              <div className="flex items-center gap-2">
                <Smartphone size={16} style={{ color: "#10b981" }} />
                <p className="text-sm font-bold" style={{ color: C.textDark, fontFamily: "'Exo 2'" }}>App Garçom</p>
              </div>
            </div>
            <img src={screenshotGarcom} alt="App garçom" className="w-full object-cover" style={{ maxHeight: 340 }} />
          </motion.div>

          {/* Tablet */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
            className="rounded-2xl overflow-hidden"
            style={{ background: C.white, border: `1px solid ${C.lightBorder}`, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
            <div className="p-4" style={{ borderBottom: `1px solid ${C.lightBorder}` }}>
              <div className="flex items-center gap-2">
                <Tablet size={16} style={{ color: "#8b5cf6" }} />
                <p className="text-sm font-bold" style={{ color: C.textDark, fontFamily: "'Exo 2'" }}>Autoatendimento</p>
              </div>
            </div>
            <img src={screenshotTablet} alt="Tablet autoatendimento" className="w-full object-cover" style={{ maxHeight: 260 }} />
          </motion.div>

          {/* CMV */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
            className="md:col-span-2 rounded-2xl overflow-hidden"
            style={{ background: C.white, border: `1px solid ${C.lightBorder}`, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}>
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.lightBorder}` }}>
              <div className="flex items-center gap-2">
                <BarChart3 size={16} style={{ color: "#ef4444" }} />
                <p className="text-sm font-bold" style={{ color: C.textDark, fontFamily: "'Exo 2'" }}>Relatório CMV — Custo de Mercadoria Vendida</p>
              </div>
              <span className="text-xs" style={{ color: C.textTertiary }}>Mar 2026</span>
            </div>
            <img src={screenshotCmv} alt="Relatório CMV" className="w-full object-cover" style={{ maxHeight: 340 }} />
          </motion.div>
        </div>
      </div>
    </section>
  );

  // ════════════════════════════════════════════════════════
  // SUPPORT — dark section for contrast
  // ════════════════════════════════════════════════════════
  const Support = () => (
    <section id="suporte" className="py-16 md:py-24 px-4 md:px-8" style={{ background: C.dark }}>
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs mb-4"
            style={{ background: C.greenBg, border: `1px solid ${C.greenBorder}`, color: C.green }}>
            <span className="w-2 h-2 rounded-full" style={{ background: C.green }} />
            Suporte 24 horas por dia, os 365 dias do ano
          </div>
          <h2 style={{ fontFamily: "'Exo 2'", fontWeight: 800, fontSize: "clamp(24px,3vw,30px)", color: "white" }}>
            Não te deixamos na mão, <span style={{ color: C.green }}>nunca.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed" style={{ color: C.textMuted }}>
            Nossa equipe está disponível 24 horas por dia, os 365 dias do ano — incluindo feriados e finais de semana.
            Problema às 23h de um domingo? A gente resolve.
          </p>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="grid grid-cols-2 gap-4">
          {[
            { icon: <MessageCircle size={20} />, title: "WhatsApp", sub: "Resposta em minutos" },
            { icon: <Phone size={20} />, title: "Telefone", sub: "Suporte direto" },
            { icon: <Video size={20} />, title: "Vídeo chamada", sub: "Treinamento online" },
            { icon: <Rocket size={20} />, title: "Implantação", sub: "Incluída no plano" },
          ].map((c, i) => (
            <motion.div key={i} variants={fadeUp} className="p-5 rounded-xl"
              style={{ background: C.darkSurface, border: `1px solid ${C.darkBorder}` }}>
              <div style={{ color: C.blue }} className="mb-3">{c.icon}</div>
              <p className="text-sm font-semibold" style={{ color: "white" }}>{c.title}</p>
              <p className="text-xs mt-1" style={{ color: C.textMuted }}>{c.sub}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );

  // ════════════════════════════════════════════════════════
  // PRICING — white bg
  // ════════════════════════════════════════════════════════
  const Pricing = () => {
    const plans = [
      {
        name: "Starter",
        price: "R$149",
        period: "/mês",
        features: ["PDV completo", "Até 2 usuários", "App do garçom", "Suporte 24h · 365 dias"],
        popular: false,
        cta: "Assinar agora",
      },
      {
        name: "Profissional",
        price: "R$249",
        period: "/mês",
        features: ["Tudo do Starter", "KDS cozinha digital", "Tablet autoatendimento", "CMV e DRE completo", "Usuários ilimitados"],
        popular: true,
        cta: "Assinar agora",
      },
      {
        name: "Enterprise",
        price: "Sob consulta",
        period: "",
        features: ["Tudo do Profissional", "Multi-unidade", "API e integrações", "Gerente de conta dedicado"],
        popular: false,
        cta: "Falar com consultor",
      },
    ];

    return (
      <section id="planos" className="py-16 md:py-24 px-4 md:px-8" style={{ background: C.white }}>
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-12">
            <p className="text-xs font-bold tracking-[3px] uppercase mb-3" style={{ color: C.blue }}>Planos</p>
            <h2 style={{ fontFamily: "'Exo 2'", fontWeight: 800, fontSize: "clamp(24px,3vw,32px)", color: C.textDark }}>
              Escolha o plano ideal <span style={{ color: C.blue }}>para seu negócio</span>
            </h2>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-5 mb-10">
            {plans.map((p, i) => (
              <motion.div key={i} variants={fadeUp}
                className="rounded-2xl p-7 flex flex-col relative transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: C.white,
                  border: p.popular ? `2px solid ${C.blue}` : `1px solid ${C.lightBorder}`,
                  boxShadow: p.popular ? "0 8px 30px rgba(0,170,255,0.15)" : "0 1px 3px rgba(0,0,0,0.06)",
                }}>
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-bold text-white"
                    style={{ background: C.blue }}>
                    Mais popular
                  </span>
                )}
                <h3 className="text-lg font-bold mb-1" style={{ color: C.textDark, fontFamily: "'Exo 2'" }}>{p.name}</h3>
                <div className="mb-5">
                  <span className="text-3xl font-extrabold" style={{ color: C.blue, fontFamily: "'Exo 2'" }}>{p.price}</span>
                  <span className="text-sm" style={{ color: C.textSecondary }}>{p.period}</span>
                </div>
                <ul className="space-y-3 mb-6 flex-1">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center gap-2.5 text-sm" style={{ color: C.textSecondary }}>
                      <Check size={16} style={{ color: C.green }} /> {f}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] mb-3 text-center" style={{ color: C.textTertiary }}>Sem fidelidade · Cancele quando quiser</p>
                <button
                  onClick={() => p.cta === "Falar com consultor" ? window.open(whatsappUrl, "_blank") : scrollTo("#contato")}
                  className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:brightness-110"
                  style={p.popular
                    ? { background: C.blue, color: "white" }
                    : { background: C.lightBg, border: `1px solid ${C.lightBorder}`, color: C.textDark }
                  }>
                  {p.cta}
                </button>
              </motion.div>
            ))}
          </motion.div>

          {/* Guarantee */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="flex items-center gap-4 p-5 rounded-2xl"
            style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
            <Shield size={28} style={{ color: "#16a34a", flexShrink: 0 }} />
            <div>
              <p className="font-bold text-base" style={{ color: "#15803d" }}>Garantia de 7 dias ou seu dinheiro de volta</p>
              <p className="text-sm mt-1" style={{ color: C.textSecondary }}>
                Assine qualquer plano com total tranquilidade. Se não gostar nos primeiros 7 dias, devolvemos 100% do valor pago.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  // ════════════════════════════════════════════════════════
  // TESTIMONIALS — light gray bg
  // ════════════════════════════════════════════════════════
  const Testimonials = () => {
    const reviews = [
      { initials: "MR", name: "Marcos Ribeiro", place: "Restaurante Sabor do Chef · SP", text: "Implantamos em 2 dias. Os garçons aprenderam em uma tarde. Nunca mais tivemos erro de pedido na cozinha." },
      { initials: "AP", name: "Ana Paula Santos", place: "Bar da Ana · Campinas", text: "O CMV mudou como gerencio meu negócio. Sei exatamente qual prato dá mais margem. Indispensável." },
      { initials: "JF", name: "José Ferreira", place: "Chopperia Boa Vista · MG", text: "O tablet aumentou nosso ticket em 23%. E o suporte resolveu um problema às 23h de um domingo em minutos." },
    ];

    return (
      <section className="py-16 md:py-24 px-4 md:px-8" style={{ background: C.lightBg }}>
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-center mb-12">
            <p className="text-xs font-bold tracking-[3px] uppercase mb-3" style={{ color: C.blue }}>Depoimentos</p>
            <h2 style={{ fontFamily: "'Exo 2'", fontWeight: 800, fontSize: "clamp(24px,3vw,32px)", color: C.textDark }}>
              O que nossos clientes <span style={{ color: C.blue }}>dizem</span>
            </h2>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-5">
            {reviews.map((r, i) => (
              <motion.div key={i} variants={fadeUp} className="p-6 rounded-2xl"
                style={{ background: C.white, border: `1px solid ${C.lightBorder}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} size={16} fill="#f59e0b" style={{ color: "#f59e0b" }} />
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-5" style={{ color: C.textSecondary }}>"{r.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: C.blue }}>
                    {r.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: C.textDark }}>{r.name}</p>
                    <p className="text-xs" style={{ color: C.textTertiary }}>{r.place}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    );
  };

  // ════════════════════════════════════════════════════════
  // CONTACT / CTA — dark for urgency
  // ════════════════════════════════════════════════════════
  const Contact = () => (
    <section id="contato" className="py-16 md:py-24 px-4 md:px-8" style={{ background: C.dark }}>
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-start">
        {/* LEFT */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="flex flex-col gap-5">
          <h2 style={{ fontFamily: "'Exo 2'", fontWeight: 800, fontSize: "clamp(24px,3vw,32px)", color: "white" }}>
            Pronto para <span style={{ color: C.blue }}>ser Altus?</span>
          </h2>
          <p className="text-base leading-relaxed" style={{ color: C.textMuted }}>
            Sem contrato, sem fidelidade, sem complicação.<br />
            Suporte 24 horas por dia, os 365 dias do ano — desde o primeiro dia.<br />
            7 dias ou seu dinheiro de volta.
          </p>

          {/* Homologados */}
          <div className="flex items-center gap-2 flex-wrap text-[12px] mt-2" style={{ color: C.textGhost }}>
            Homologado:
            {[
              { name: "Stone", color: C.green },
              { name: "PagSeguro", color: "#f5a623" },
              { name: "SmartPOS", color: C.cyan },
            ].map(a => (
              <span key={a.name} className="px-2.5 py-0.5 rounded-full text-[11px]" style={{ color: a.color, border: `1px solid ${a.color}30` }}>
                {a.name}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => scrollTo("#planos")}
              className="px-7 py-3.5 rounded-xl font-bold text-[15px] transition-all hover:brightness-110 text-white"
              style={{ background: C.blue, boxShadow: "0 4px 20px rgba(0,170,255,0.35)" }}>
              Assinar agora
            </button>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-12 h-12 rounded-xl transition-all hover:scale-105"
              style={{ background: C.greenBg, border: `1px solid ${C.greenBorder}` }}
              aria-label="Falar no WhatsApp">
              <WaSvg />
            </a>
          </div>
        </motion.div>

        {/* RIGHT — Form */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="rounded-2xl p-6" style={{ background: C.darkSurface, border: `1px solid ${C.darkBorder}`, maxWidth: 440 }}>
          <h3 className="text-lg font-bold mb-1" style={{ fontFamily: "'Exo 2'", color: C.cyan }}>Fale com a gente</h3>
          <p className="text-xs mb-5" style={{ color: C.textMuted }}>Retornamos em até 30 minutos · Suporte 24h · 365 dias</p>

          {formSent ? (
            <div className="text-center py-8">
              <Check size={40} style={{ color: C.green }} className="mx-auto mb-3" />
              <p className="font-semibold" style={{ color: C.green }}>Mensagem enviada!</p>
              <p className="text-xs mt-2" style={{ color: C.textMuted }}>Retornaremos em breve.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input {...register("nome", { required: true })} placeholder="Nome *"
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1"
                  style={{ background: C.darkCard, border: `1px solid ${errors.nome ? "#ef4444" : C.darkBorder}`, color: C.textLight, "--tw-ring-color": C.blue } as any} />
                <input {...register("email")} placeholder="E-mail" type="email"
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1"
                  style={{ background: C.darkCard, border: `1px solid ${C.darkBorder}`, color: C.textLight, "--tw-ring-color": C.blue } as any} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input {...register("whatsapp")} placeholder="WhatsApp"
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1"
                  style={{ background: C.darkCard, border: `1px solid ${C.darkBorder}`, color: C.textLight, "--tw-ring-color": C.blue } as any} />
                <input {...register("cidade")} placeholder="Cidade"
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1"
                  style={{ background: C.darkCard, border: `1px solid ${C.darkBorder}`, color: C.textLight, "--tw-ring-color": C.blue } as any} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input {...register("estabelecimento")} placeholder="Nome do estabelecimento"
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1"
                  style={{ background: C.darkCard, border: `1px solid ${C.darkBorder}`, color: C.textLight, "--tw-ring-color": C.blue } as any} />
                <select {...register("tipo", { required: true })}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1"
                  style={{ background: C.darkCard, border: `1px solid ${errors.tipo ? "#ef4444" : C.darkBorder}`, color: C.textLight, "--tw-ring-color": C.blue } as any}>
                  <option value="">Tipo *</option>
                  {["Restaurante", "Bar", "Lanchonete", "Pizzaria", "Hamburgueria", "Churrascaria", "Café", "Outro"].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <textarea {...register("mensagem")} placeholder="Mensagem (opcional)" rows={3}
                className="w-full px-3 py-2.5 rounded-lg text-sm outline-none focus:ring-1 resize-none"
                style={{ background: C.darkCard, border: `1px solid ${C.darkBorder}`, color: C.textLight, "--tw-ring-color": C.blue } as any} />

              {formError && <p className="text-xs" style={{ color: "#ef4444" }}>{formError}</p>}

              <button type="submit" disabled={isSubmitting}
                className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:brightness-110 disabled:opacity-50 text-white"
                style={{ background: C.blue }}>
                {isSubmitting ? "Enviando..." : "Enviar mensagem"}
              </button>

              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110"
                style={{ background: C.greenBg, border: `1px solid ${C.greenBorder}`, color: C.green }}>
                <WaSvg size={16} />
                Prefiro conversar pelo WhatsApp
              </a>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );

  // ════════════════════════════════════════════════════════
  // FOOTER — dark
  // ════════════════════════════════════════════════════════
  const Footer = () => (
    <footer className="py-8 px-4 md:px-8" style={{ background: "#060a14", borderTop: `1px solid ${C.darkBorder}` }}>
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex flex-col items-center md:items-start gap-1">
          <span style={{ fontFamily: "'Exo 2'", fontWeight: 800, color: C.blue, fontSize: 16 }}>
            ALTUS <span style={{ fontWeight: 400, color: C.cyan }}>Food</span>
          </span>
          <p className="text-[11px]" style={{ color: C.textGhost }}>
            © 2026 Actual TI · sejaaltus.com.br · contato@actualti.com.br
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs" style={{ color: C.textMuted }}>
          <Link to="/privacidade" className="hover:text-white transition-colors">Privacidade</Link>
          <span className="cursor-default">Termos</span>
          <button onClick={() => scrollTo("#suporte")} className="hover:text-white transition-colors">Suporte</button>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg transition-all hover:scale-105"
            style={{ background: C.greenBg, border: `1px solid ${C.greenBorder}` }}
            aria-label="Falar no WhatsApp">
            <WaSvg size={18} />
          </a>
        </div>
      </div>
    </footer>
  );

  return (
    <div style={{ fontFamily: "Nunito" }}>
      <Navbar />
      <Hero />
      <Stats />
      <Features />
      <SystemScreens />
      <Support />
      <Pricing />
      <Testimonials />
      <Contact />
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Landing;
